import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const LangContext = createContext({ lang: "en", setLang: () => {}, toggle: () => {}, t: {} });

const dict = {
  en: {
    nav: { home: "Home", services: "Services", about: "About", contact: "Contact" },
    common: {
      callNow: "Call Now",
      call: "Call",
      getFreeQuote: "Get a Free Quote",
      requestService: "Request Service",
      requestServiceLower: "Request service",
      readOnGoogle: "Read on Google",
      readMoreOnGoogle: "Read more on Google",
      seeAllServices: "See all services",
      switchToDark: "Switch to dark mode",
      switchToLight: "Switch to light mode",
      switchLanguage: "Switch to Spanish",
      openMenu: "Open menu",
      closeMenu: "Close menu",
      and: "and",
    },
    hero: {
      overline: "Houston, TX · Independent HVAC Technicians",
      headlineA: "Keeping Houston",
      headlineCool: "Cool",
      headlineComma: ",",
      headlineB: "All Year Long.",
      subheadline:
        "From mini-splits to whole-home central A/C, the HOUSTO A/C team installs, repairs and maintains every brand — with same-day response and honest, upfront pricing.",
      trust1: "Independent Technicians",
      trust2: "Same-Day Response",
      trust3: "Houston & Surrounding",
      badgeNow: "Right Now",
      badgeText: "Booking same-day repairs across Houston",
    },
    why: {
      overline: "Why HOUSTO A/C",
      title: "Built for Houston heat. Trusted by your neighbors.",
      items: [
        { title: "Fast Response", desc: "Same-day service across Houston. We know how brutal Texas heat can be." },
        { title: "Independent Technicians", desc: "We're an independent crew — direct communication, no middlemen, and no corporate overhead inflating your bill." },
        { title: "Residential & Commercial", desc: "From bedroom mini-splits to rooftop commercial units — we do it all." },
        { title: "Free Estimates", desc: "Honest, upfront pricing on installs and replacements. Zero hidden fees." },
      ],
    },
    gallery: {
      overline: "Our Recent Work",
      title: "Real installs. Real Houston homes.",
      captions: [
        "Mini-Split Install — Rustic Workshop",
        "Aurus Inverter — Exterior Line Set",
        "Confortotal Condenser — Commercial",
        "Central AC Condenser — Residential",
        "Goodman Air Handler — Attic Install",
      ],
      jobPhoto: "HOUSTO A/C · Job Photo",
    },
    testimonials: {
      overline: "What neighbors say",
      title: "5-star service across Houston.",
      basedOn: (n) => `Based on ${n}+ verified customer reviews`,
      items: [
        {
          name: "Maria G.",
          city: "Katy, TX",
          text: "Our A/C died on the hottest day of July and HOUSTO had a tech at the house within two hours. Honest diagnosis, fair price, fixed in one visit. Won't call anyone else.",
        },
        {
          name: "James R.",
          city: "Sugar Land, TX",
          text: "Got three quotes for a full central A/C replacement — HOUSTO was the only one that actually explained what they were installing and why. Clean install, no upsells, the house is freezing now.",
        },
        {
          name: "Brenda T.",
          city: "Houston Heights, TX",
          text: "Had a mini-split installed in our converted garage workshop. Showed up on time, kept the place spotless, and walked me through every step. Highly recommended.",
        },
        {
          name: "Carlos M.",
          city: "Pearland, TX",
          text: "Preventive maintenance plan saved us from a meltdown. Found a weak capacitor before it failed — saved a $400 emergency call. Professional crew, locally owned, genuinely care.",
        },
      ],
    },
    services: {
      overline: "What we do",
      titleHome: "Full-service air conditioning.",
      titlePage: "Every A/C job, done right the first time.",
      overlinePage: "Our HVAC Services",
      subtitlePage:
        "Residential and commercial cooling for Houston and the surrounding Gulf Coast. Pick a service below or call us for a free phone estimate.",
      items: [
        { title: "Mini-Split Installation", description: "Ductless comfort for additions, garages, and zone-controlled rooms. We size, install, and commission every system to manufacturer spec." },
        { title: "Central AC Installation", description: "Whole-home cooling installs with high-SEER condensers, evaporator coils, and proper refrigerant charging for peak Houston efficiency." },
        { title: "Air Handler Installation", description: "Goodman, Carrier, and Trane air handler replacements with proper plenum transitions, drainage, and electrical hookup." },
        { title: "Ductwork Installation & Repair", description: "New duct runs, sealed plenums, and leaky-duct repairs that recover lost airflow and lower your monthly cooling bill." },
        { title: "AC Repair", description: "Capacitors, fan motors, contactors, refrigerant leaks, frozen coils — fast diagnostics, honest fixes, no upsells." },
        { title: "Preventive Maintenance", description: "Seasonal tune-ups, coil cleaning, refrigerant checks, and electrical inspections to extend equipment life and prevent breakdowns." },
      ],
      bottomCtaTitle: "Need it fixed today?",
      bottomCtaDesc: "Call now — we usually answer on the first ring.",
      otherOption: "Other / Not Sure",
    },
    ctaStrip: {
      title: "A/C trouble? Don't sweat it.",
      subtitle: "Call us now for a free phone estimate, or send us a message and we'll be in touch within hours.",
    },
    about: {
      overline: "About HOUSTO A/C",
      title: "Your local Houston HVAC team.",
      p1: "HOUSTO A/C is a Houston-area air conditioning company built on three things: showing up fast, doing quality work, and quoting honest prices. We're a small, hands-on team — you'll know your technician's name, and you'll see us again next season.",
      p2pre: "Whether it's a sweltering July repair, a brand-new mini-split install, or a duct system that's been leaking for years, we treat every job like it matters — because it does. We serve ",
      p2bold: "Houston and all surrounding areas",
      p2post: ", including ",
      areaBadge: "& surrounding areas",
      areaCity: "Houston, TX",
      valuesOverline: "What we stand for",
      valuesTitle: "The values behind every service call.",
      values: [
        { title: "Locally Owned", desc: "Born and raised in the Houston area — we treat your home like it's our neighbor's." },
        { title: "Quality Work", desc: "Manufacturer-spec installs, properly charged refrigerant, and clean job sites every time." },
        { title: "Honest Pricing", desc: "Flat-rate estimates, no surprise fees, no high-pressure sales tactics. Ever." },
        { title: "Customer-First", desc: "We earn 5-star reviews the slow way — by showing up on time and doing the job right." },
      ],
    },
    contact: {
      overline: "Contact / Free Quote",
      title: "Get a free quote. Fast.",
      subtitle:
        "Tell us about your A/C issue or project and we'll text or call you back the same day — usually within a couple of hours.",
      talkTitle: "Talk to a tech now",
      talkDesc: "Click-to-call — we usually pick up on the first ring.",
      serviceAreaTitle: "Service Area",
      serviceAreaDescPre: "We serve ",
      serviceAreaBold: "Houston and all surrounding areas",
      serviceAreaDescPost: ", including:",
      expectTitle: "What to expect",
      expect: [
        "Same-day callback during business hours",
        "Free phone estimate for installs & replacements",
        "Independent technicians — direct, personal service",
      ],
      labels: {
        name: "Full Name",
        phone: "Phone",
        email: "Email",
        service: "Service Needed",
        message: "Message",
      },
      placeholders: {
        name: "Jane Doe",
        phone: "(346) 555-1234",
        email: "you@email.com",
        service: "Pick the service you need…",
        message: "Tell us a bit about your system, the issue, or your project…",
      },
      submit: "Request My Free Quote",
      submitting: "Sending…",
      footnote:
        "We serve Houston and all surrounding areas. No spam — your info is only used to respond to your request.",
      successTitle: "Got it — we're on it.",
      successDesc:
        "Your request was received. A HOUSTO A/C team member will reach out shortly. For urgent A/C failures, please call us directly.",
      submitAnother: "Submit another request",
      validationError: "Please complete all fields before submitting.",
      successToast: "Thanks! We received your request — we'll reach out shortly.",
      errorToast: "Something went wrong. Please call us instead.",
    },
    footer: {
      about:
        "Local Houston HVAC. Honest pricing. Fast response. We install, repair, and maintain residential and commercial air conditioning across the greater Houston metro.",
      serviceAreas: "Service Areas",
      contact: "Contact",
      getQuote: "Get a Free Quote →",
      copyright: "All rights reserved.",
      tagline: "Independent Technicians · Serving Houston & Surrounding Areas",
    },
  },
  es: {
    nav: { home: "Inicio", services: "Servicios", about: "Nosotros", contact: "Contacto" },
    common: {
      callNow: "Llamar Ahora",
      call: "Llamar",
      getFreeQuote: "Cotización Gratis",
      requestService: "Solicitar Servicio",
      requestServiceLower: "Solicitar servicio",
      readOnGoogle: "Ver en Google",
      readMoreOnGoogle: "Ver más en Google",
      seeAllServices: "Ver todos los servicios",
      switchToDark: "Cambiar a modo oscuro",
      switchToLight: "Cambiar a modo claro",
      switchLanguage: "Cambiar a inglés",
      openMenu: "Abrir menú",
      closeMenu: "Cerrar menú",
      and: "y",
    },
    hero: {
      overline: "Houston, TX · Técnicos HVAC Independientes",
      headlineA: "Manteniendo Houston",
      headlineCool: "Fresco",
      headlineComma: ",",
      headlineB: "Todo el Año.",
      subheadline:
        "Desde mini-splits hasta sistemas centrales para toda la casa, el equipo de HOUSTO A/C instala, repara y mantiene cualquier marca — con respuesta el mismo día y precios honestos y claros.",
      trust1: "Técnicos Independientes",
      trust2: "Respuesta el Mismo Día",
      trust3: "Houston y Alrededores",
      badgeNow: "Ahora Mismo",
      badgeText: "Agendando reparaciones el mismo día en Houston",
    },
    why: {
      overline: "Por qué HOUSTO A/C",
      title: "Hecho para el calor de Houston. La confianza de tus vecinos.",
      items: [
        { title: "Respuesta Rápida", desc: "Servicio el mismo día en Houston. Sabemos lo brutal que puede ser el calor de Texas." },
        { title: "Técnicos Independientes", desc: "Somos un equipo independiente — comunicación directa, sin intermediarios y sin sobrecostos corporativos en tu factura." },
        { title: "Residencial y Comercial", desc: "Desde mini-splits en recámaras hasta unidades comerciales en techo — lo hacemos todo." },
        { title: "Cotizaciones Gratis", desc: "Precios honestos y claros en instalaciones y reemplazos. Cero cargos ocultos." },
      ],
    },
    gallery: {
      overline: "Trabajos Recientes",
      title: "Instalaciones reales. Hogares reales de Houston.",
      captions: [
        "Mini-Split — Taller Rústico",
        "Inverter Aurus — Línea Exterior",
        "Condensador Confortotal — Comercial",
        "Condensador A/C Central — Residencial",
        "Manejador de Aire Goodman — Ático",
      ],
      jobPhoto: "HOUSTO A/C · Foto de Trabajo",
    },
    testimonials: {
      overline: "Lo que dicen los vecinos",
      title: "Servicio 5 estrellas en todo Houston.",
      basedOn: (n) => `Basado en ${n}+ reseñas verificadas de clientes`,
      items: [
        {
          name: "María G.",
          city: "Katy, TX",
          text: "Nuestro A/C se descompuso el día más caluroso de julio y HOUSTO tuvo un técnico en casa en dos horas. Diagnóstico honesto, precio justo, resuelto en una sola visita. No llamaré a nadie más.",
        },
        {
          name: "James R.",
          city: "Sugar Land, TX",
          text: "Pedí tres cotizaciones para reemplazar el A/C central — HOUSTO fue el único que realmente me explicó qué iban a instalar y por qué. Instalación limpia, sin ventas agresivas, la casa está helada ahora.",
        },
        {
          name: "Brenda T.",
          city: "Houston Heights, TX",
          text: "Instalaron un mini-split en mi taller de garage. Llegaron puntuales, dejaron todo impecable y me explicaron cada paso. Muy recomendados.",
        },
        {
          name: "Carlos M.",
          city: "Pearland, TX",
          text: "El plan de mantenimiento preventivo nos salvó de una crisis. Detectaron un capacitor débil antes de que fallara — nos ahorramos una llamada de emergencia de $400. Equipo profesional, local, y de verdad les importa.",
        },
      ],
    },
    services: {
      overline: "Lo que hacemos",
      titleHome: "Aire acondicionado de servicio completo.",
      titlePage: "Cada trabajo de A/C, bien hecho desde la primera vez.",
      overlinePage: "Nuestros Servicios HVAC",
      subtitlePage:
        "Refrigeración residencial y comercial para Houston y toda la Costa del Golfo. Elige un servicio o llámanos para una cotización telefónica gratuita.",
      items: [
        { title: "Instalación de Mini-Split", description: "Confort sin ductos para ampliaciones, garajes y habitaciones con control por zonas. Calculamos, instalamos y configuramos cada sistema según las especificaciones del fabricante." },
        { title: "Instalación de A/C Central", description: "Instalaciones para toda la casa con condensadores de alto SEER, serpentines evaporadores y carga adecuada de refrigerante para máxima eficiencia en Houston." },
        { title: "Instalación de Manejador de Aire", description: "Reemplazos de manejadores de aire Goodman, Carrier y Trane con transiciones de plenum adecuadas, drenaje y conexión eléctrica." },
        { title: "Instalación y Reparación de Ductos", description: "Nuevos ductos, plenums sellados y reparaciones de fugas que recuperan el flujo de aire perdido y reducen tu factura mensual." },
        { title: "Reparación de A/C", description: "Capacitores, motores de ventilador, contactores, fugas de refrigerante, serpentines congelados — diagnóstico rápido, reparaciones honestas, sin ventas adicionales." },
        { title: "Mantenimiento Preventivo", description: "Afinaciones de temporada, limpieza de serpentines, revisión de refrigerante e inspecciones eléctricas para extender la vida del equipo y prevenir fallas." },
      ],
      bottomCtaTitle: "¿Necesitas reparación hoy?",
      bottomCtaDesc: "Llama ahora — solemos contestar al primer timbre.",
      otherOption: "Otro / No Estoy Seguro",
    },
    ctaStrip: {
      title: "¿Problemas con tu A/C? No te preocupes.",
      subtitle:
        "Llámanos ahora para una cotización telefónica gratis, o envíanos un mensaje y te contactaremos en horas.",
    },
    about: {
      overline: "Sobre HOUSTO A/C",
      title: "Tu equipo local de HVAC en Houston.",
      p1: "HOUSTO A/C es una empresa de aire acondicionado del área de Houston construida sobre tres pilares: llegar rápido, hacer trabajo de calidad y dar precios honestos. Somos un equipo pequeño y práctico — vas a conocer el nombre de tu técnico, y nos verás de nuevo la próxima temporada.",
      p2pre:
        "Ya sea una reparación urgente en julio, una instalación nueva de mini-split, o un sistema de ductos con fugas desde hace años, tratamos cada trabajo como si importara — porque sí importa. Servimos a ",
      p2bold: "Houston y todas las áreas circundantes",
      p2post: ", incluyendo ",
      areaBadge: "y alrededores",
      areaCity: "Houston, TX",
      valuesOverline: "En lo que creemos",
      valuesTitle: "Los valores detrás de cada llamada de servicio.",
      values: [
        { title: "Propiedad Local", desc: "Nacidos y criados en Houston — tratamos tu hogar como el de un vecino." },
        { title: "Trabajo de Calidad", desc: "Instalaciones según especificaciones, refrigerante correctamente cargado y sitios de trabajo limpios siempre." },
        { title: "Precios Honestos", desc: "Cotizaciones a precio fijo, sin cargos sorpresa, sin ventas agresivas. Nunca." },
        { title: "Cliente Primero", desc: "Ganamos reseñas de 5 estrellas a la antigua — llegando a tiempo y haciendo bien el trabajo." },
      ],
    },
    contact: {
      overline: "Contacto / Cotización Gratis",
      title: "Obtén una cotización gratis. Rápido.",
      subtitle:
        "Cuéntanos sobre tu problema o proyecto de A/C y te contactaremos por mensaje o llamada el mismo día — usualmente en un par de horas.",
      talkTitle: "Habla con un técnico ahora",
      talkDesc: "Llamada directa — solemos contestar al primer timbre.",
      serviceAreaTitle: "Área de Servicio",
      serviceAreaDescPre: "Servimos ",
      serviceAreaBold: "Houston y todas las áreas circundantes",
      serviceAreaDescPost: ", incluyendo:",
      expectTitle: "Qué esperar",
      expect: [
        "Devolución de llamada el mismo día en horario laboral",
        "Cotización telefónica gratuita para instalaciones y reemplazos",
        "Técnicos independientes — servicio directo y personal",
      ],
      labels: {
        name: "Nombre Completo",
        phone: "Teléfono",
        email: "Correo Electrónico",
        service: "Servicio Necesario",
        message: "Mensaje",
      },
      placeholders: {
        name: "Juana Pérez",
        phone: "(346) 555-1234",
        email: "tu@correo.com",
        service: "Elige el servicio que necesitas…",
        message: "Cuéntanos un poco sobre tu sistema, el problema o tu proyecto…",
      },
      submit: "Solicitar Mi Cotización Gratis",
      submitting: "Enviando…",
      footnote:
        "Servimos Houston y todas las áreas circundantes. Sin spam — tu información solo se usa para responder a tu solicitud.",
      successTitle: "¡Listo — estamos en eso!",
      successDesc:
        "Tu solicitud fue recibida. Un miembro del equipo de HOUSTO A/C te contactará pronto. Para fallas urgentes de A/C, por favor llámanos directamente.",
      submitAnother: "Enviar otra solicitud",
      validationError: "Por favor completa todos los campos antes de enviar.",
      successToast: "¡Gracias! Recibimos tu solicitud — te contactaremos pronto.",
      errorToast: "Algo salió mal. Por favor llámanos directamente.",
    },
    footer: {
      about:
        "HVAC local en Houston. Precios honestos. Respuesta rápida. Instalamos, reparamos y mantenemos aire acondicionado residencial y comercial en todo el área metropolitana de Houston.",
      serviceAreas: "Áreas de Servicio",
      contact: "Contacto",
      getQuote: "Cotización Gratis →",
      copyright: "Todos los derechos reservados.",
      tagline: "Técnicos Independientes · Sirviendo Houston y Alrededores",
    },
  },
};

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return "en";
    const saved = localStorage.getItem("houstoac_lang");
    if (saved === "en" || saved === "es") return saved;
    // Default to browser preference if it starts with es
    const nav = (navigator.language || "en").toLowerCase();
    return nav.startsWith("es") ? "es" : "en";
  });

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next) => {
    setLangState(next);
    try {
      localStorage.setItem("houstoac_lang", next);
    } catch (e) {
      // ignore
    }
  }, []);

  const toggle = useCallback(() => {
    setLang(lang === "en" ? "es" : "en");
  }, [lang, setLang]);

  const t = dict[lang];

  return (
    <LangContext.Provider value={{ lang, setLang, toggle, t }}>
      {children}
    </LangContext.Provider>
  );
}

export function useLang() {
  return useContext(LangContext);
}
